/**
 * MissionVoiceSynthesis — Affiche la synthèse vocale validée par le client
 * pour une mission donnée. Visible côté spécialiste (lecture seule) et côté client (lecture).
 *
 * Affichage premium institutionnel : badge "Synthèse client validée" + 
 * blocs structurés (chronologie / faits / urgence / docs / actions / pro suggéré).
 */
import { useEffect, useState } from "react";
import {
  ShieldCheck, Loader2, Phone, Clock, AlertTriangle, FileText, Calendar,
  CheckCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { apiClient } from "@/lib/api";

const URGENCY_COLORS = {
  faible: "bg-[#063D25]/60 text-emerald-700 border-emerald-200",
  modérée: "bg-[#F5C400]/10 text-[#F5C400] border-[#F5C400]/30",
  élevée: "bg-red-900/20 text-red-700 border-red-900/40",
};

export default function MissionVoiceSynthesis({ missionId, viewer = "expert" }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(viewer === "expert");

  useEffect(() => {
    if (!missionId) return;
    apiClient.get(`/voice/missions/${missionId}/synthesis`)
      .then(r => setData(r.data))
      .catch(() => setData({ available: false, error: true }))
      .finally(() => setLoading(false));
  }, [missionId]);

  if (loading) {
    return (
      <Card className="p-5 rounded-xl border-slate-100 bg-[#031B12]">
        <Loader2 className="w-4 h-4 animate-spin text-[#BFC8C2]/70" />
      </Card>
    );
  }

  if (!data || !data.available) {
    if (viewer === "expert") {
      return (
        <Card className="p-5 rounded-xl border-dashed border-[#063D25] bg-[#031B12]/50" data-testid="mission-no-synthesis">
          <div className="flex items-start gap-3">
            <Phone className="w-4 h-4 text-[#BFC8C2]/70 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium cb-text-ivory">Aucune synthèse vocale transmise</p>
              <p className="text-xs text-[#BFC8C2]/85 mt-1">
                Le client n'a pas (encore) transmis de synthèse vocale CONFILEX pour ce dossier.
              </p>
            </div>
          </div>
        </Card>
      );
    }
    return null;
  }

  const s = data.synthesis || {};

  return (
    <Card className="rounded-xl border-[#F5C400]/30 bg-gradient-to-br from-[#F7F7F2]/40 to-white overflow-hidden" data-testid="mission-voice-synthesis">
      <div className="bg-gradient-to-r from-[#031B12] to-[#063D25] text-white px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#F5C400]/20 flex items-center justify-center">
              <Phone className="w-4 h-4 text-[#F5C400]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#F5C400]">Synthèse vocale validée</p>
              <h3 className="font-serif italic text-base">Préqualification CONFILEX</h3>
            </div>
          </div>
          <button
            onClick={() => setExpanded(e => !e)}
            data-testid="mission-synthesis-toggle"
            className="text-white/70 hover:text-white inline-flex items-center gap-1 text-xs"
          >
            {expanded ? <>Réduire <ChevronUp className="w-3 h-3" /></> : <>Déployer <ChevronDown className="w-3 h-3" /></>}
          </button>
        </div>
        <div className="flex items-center gap-3 mt-3 text-[10px] text-white/60">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(data.call_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
          </span>
          {data.duration_sec != null && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {Math.round(data.duration_sec / 60)} min
            </span>
          )}
          {data.consent_recording && (
            <span className="inline-flex items-center gap-1 text-emerald-200">
              <ShieldCheck className="w-3 h-3" /> Enregistré (RGPD)
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[#F5C400]">
            <CheckCircle className="w-3 h-3" /> Validée par le client
          </span>
        </div>
      </div>

      {expanded && (
        <div className="px-5 py-5 space-y-4">
          {s.summary_one_paragraph && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-1.5">En une phrase</p>
              <p className="text-sm text-[#031B12] italic font-serif leading-relaxed" data-testid="syn-display-summary">
                « {s.summary_one_paragraph} »
              </p>
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-4">
            {s.urgency_level && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-1.5">Urgence</p>
                <span className={`inline-block text-xs px-3 py-1 rounded-full border ${URGENCY_COLORS[s.urgency_level] || URGENCY_COLORS.faible}`}>
                  {s.urgency_level}
                </span>
              </div>
            )}
            {s.case_type && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-1.5">Type de dossier</p>
                <p className="text-sm text-[#031B12]">{s.case_type}</p>
              </div>
            )}
            {s.professional_type_suggested && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-1.5">Profil suggéré</p>
                <p className="text-sm text-[#031B12]">{s.professional_type_suggested}</p>
              </div>
            )}
          </div>

          {Array.isArray(s.chronology) && s.chronology.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-2">Chronologie</p>
              <ul className="space-y-1.5">
                {s.chronology.map((c, i) => (
                  <li key={`chrono-${i}-${(c.date || c.fact || "").substring(0, 8)}`} className="text-sm text-[#031B12] flex gap-2">
                    {c.date && <span className="text-[10px] uppercase tracking-wider text-[#F5C400] shrink-0 mt-0.5">{c.date}</span>}
                    <span>{c.fact}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(s.key_facts) && s.key_facts.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-2">Faits importants</p>
              <ul className="space-y-1 text-sm text-[#031B12]">
                {s.key_facts.map((f, i) => <li key={`fact-${i}-${f.substring(0, 10)}`}>• {f}</li>)}
              </ul>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {Array.isArray(s.documents_available) && s.documents_available.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-2 inline-flex items-center gap-1.5">
                  <FileText className="w-3 h-3 text-[#2ECC71]" /> Documents disponibles
                </p>
                <ul className="space-y-1 text-xs cb-text-ivory">
                  {s.documents_available.map((d, i) => <li key={`doc-av-${i}`}>✓ {d}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(s.documents_missing) && s.documents_missing.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-2 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 text-[#F5C400]" /> Documents manquants
                </p>
                <ul className="space-y-1 text-xs cb-text-ivory">
                  {s.documents_missing.map((d, i) => <li key={`doc-miss-${i}`}>· {d}</li>)}
                </ul>
              </div>
            )}
          </div>

          {Array.isArray(s.suggested_actions) && s.suggested_actions.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#BFC8C2]/85 mb-2">Actions suggérées</p>
              <ul className="space-y-1 text-sm text-[#031B12]">
                {s.suggested_actions.map((a, i) => <li key={`act-${i}-${a.substring(0, 8)}`}>→ {a}</li>)}
              </ul>
            </div>
          )}

          <p className="text-[10px] text-[#BFC8C2]/70 italic mt-4 border-t border-slate-100 pt-3">
            {s.disclaimer || "Synthèse générée à partir d'un échange vocal et validée par le client. Document indicatif."}
          </p>
        </div>
      )}
    </Card>
  );
}
